import React, { useEffect, useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import UpgradeNotice, { UpgradeNoticeLink } from "../../components/common/UpgradeNotice";
import useActiveProfileOrgProOrElite from "../../components/common/hooks/useActiveProfileOrgProOrElite";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import { GlobalStyles } from "../../constants/styles";
import useTranslation from "../../i18n/useTranslation";

const PROVIDERS = gql`query YouTubeProviders { youtubeProviders { id name streamingAccountId isAngeonxDefault availabilityStatus availabilityCheckedAt availabilityMessage } }`;
const CREATE_PROVIDER = gql`mutation CreateYoutubeProvider($name:String!,$provider:String!,$clientId:String!,$clientSecret:String!,$refreshToken:String!){createYoutubeProvider(name:$name,provider:$provider,clientId:$clientId,clientSecret:$clientSecret,refreshToken:$refreshToken){provider{id name streamingAccountId isAngeonxDefault}}}`;
const UPDATE_PROVIDER = gql`mutation UpdateYoutubeProvider($id:Int!,$name:String!,$provider:String!,$clientId:String!,$clientSecret:String!,$refreshToken:String!){updateYoutubeProvider(id:$id,name:$name,provider:$provider,clientId:$clientId,clientSecret:$clientSecret,refreshToken:$refreshToken){provider{id name streamingAccountId isAngeonxDefault}}}`;
const DELETE_PROVIDER = gql`mutation DeleteYoutubeProvider($id:Int!){deleteYoutubeProvider(id:$id){success}}`;
const TEST_PROVIDER = gql`mutation TestYoutubeProvider($id:Int!){testYoutubeProvider(id:$id){success message availabilityStatus availabilityCheckedAt}}`;

const emptyForm = (providerType = "angeonx") => ({
    providerType,
    name: providerType === "angeonx" ? "AngeonX YouTube Provider" : "",
    clientId: "",
    clientSecret: "",
    refreshToken: "",
});

function StreamProviderModal({ show, provider, onClose, onSave }) {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const editing = Boolean(provider);
    const modalStyles = useMemo(() => ({
        wrap: {
            borderRadius: 16,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
        },
        header: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 14px",
            background: `linear-gradient(135deg, ${alphaFromHex(colors.primary, 0.50)}, ${alphaFromHex(colors.primary, 0.00)})`,
            borderBottom: "1px solid rgba(0,0,0,0.06)",
        },
        titleWrap: { display: "flex", alignItems: "center", gap: 10 },
        badge: {
            fontSize: 12,
            fontWeight: 900,
            letterSpacing: "0.04em",
            padding: "6px 10px",
            borderRadius: 999,
            color: colors.textOnPrimary,
            background: colors.primary,
            border: "1px solid rgba(0,0,0,0.10)",
        },
        title: { margin: 0, fontSize: 16, fontWeight: 900, color: "#14151a" },
        closeBtn: {
            width: 36,
            height: 36,
            borderRadius: 10,
            border: "1px solid rgba(0,0,0,0.10)",
            background: "#fff",
            color: "#14151a",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
        },
        body: { padding: 14, background: "#fff" },
        card: {
            borderRadius: 14,
            padding: 12,
            border: "1px solid rgba(0,0,0,0.06)",
            background: "rgba(0,0,0,0.015)",
        },
        sectionTitle: {
            margin: "2px 0 10px",
            fontSize: 13,
            fontWeight: 900,
            color: GlobalStyles.colors.woodsmoke,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
        },
        label: { fontSize: 12.5, fontWeight: 800, color: "rgba(20,21,26,0.70)", marginBottom: 6 },
        input: {
            borderRadius: 12,
            border: "1px solid rgba(0,0,0,0.12)",
            padding: "10px 12px",
            fontWeight: 700,
            outline: "none",
            height: "auto",
        },
        select: {
            borderRadius: 12,
            border: "1px solid rgba(0,0,0,0.12)",
            padding: "10px 12px",
            fontWeight: 800,
            outline: "none",
            backgroundColor: "#fff",
            height: "auto",
        },
        hint: { marginTop: 6, color: "rgba(20,21,26,0.55)", fontSize: 12.5, fontWeight: 600, lineHeight: 1.25 },
        footer: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 14px",
            background: "rgba(0,0,0,0.02)",
            borderTop: "1px solid rgba(0,0,0,0.06)",
        },
        button: {
            borderRadius: 12,
            padding: "10px 12px",
            fontWeight: 900,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
        },
        buttonGhost: { background: "#fff", border: "1px solid rgba(0,0,0,0.12)", color: "#14151a" },
        buttonPrimary: {
            background: GlobalStyles.colors.canary,
            border: "1px solid rgba(0,0,0,0.10)",
            color: GlobalStyles.colors.woodsmoke,
        },
    }), [colors.primary, colors.textOnPrimary]);

    useEffect(() => {
        if (!show) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [show]);

    useEffect(() => {
        setForm(provider
            ? { ...emptyForm("youtube"), name: provider.name }
            : emptyForm());
    }, [provider, show]);

    if (!show) return null;

    const submit = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const saved = await onSave({ ...form, id: provider?.id });
            if (saved) onClose();
        } catch {
            return;
        } finally {
            setSaving(false);
        }
    };

    const showCredentials = form.providerType === "youtube";

    return <>
        <div className="modal-backdrop fade show" style={{ backgroundColor: "rgba(0,0,0,0.55)" }} onClick={saving ? undefined : onClose}/>
        <div className="modal fade show d-block" role="dialog" aria-modal="true" aria-labelledby="stream-provider-modal-title" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document" style={{ maxWidth: 720 }}>
                <form className="modal-content" style={modalStyles.wrap} onSubmit={submit}>
                    <div className="modal-header" style={modalStyles.header}>
                        <div style={modalStyles.titleWrap}>
                            <span style={modalStyles.badge}>{editing ? t("streamProvidersPage.editBadge") : t("streamProvidersPage.newBadge")}</span>
                            <h4 className="modal-title" id="stream-provider-modal-title" style={modalStyles.title}>{editing ? t("streamProvidersPage.editTitle") : t("streamProvidersPage.addTitle")}</h4>
                        </div>
                        <button type="button" aria-label={t("streamProvidersPage.close")} onClick={onClose} disabled={saving} style={modalStyles.closeBtn}>
                            <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>&times;</span>
                        </button>
                    </div>
                    <div className="modal-body" style={modalStyles.body}>
                        <div style={modalStyles.card}>
                            <div style={modalStyles.sectionTitle}>{t("streamProvidersPage.providerDetails")}</div>
                        <div className="form-group" style={{ marginBottom: showCredentials ? 12 : 0 }}>
                            <label htmlFor="stream-provider-type" style={modalStyles.label}>{t("streamProvidersPage.provider")}</label>
                            <select
                                id="stream-provider-type"
                                className="form-control"
                                style={modalStyles.select}
                                value={form.providerType}
                                disabled={editing || saving}
                                onChange={(event) => setForm(emptyForm(event.target.value))}
                            >
                                <option value="angeonx">{t("streamProvidersPage.angeonxProvider")}</option>
                                <option value="youtube">{t("streamProvidersPage.ownProvider")}</option>
                            </select>
                        </div>
                        {showCredentials ? <>
                            <div className="form-group" style={{ marginBottom: 12 }}>
                                <label htmlFor="stream-provider-name" style={modalStyles.label}>{t("streamProvidersPage.accountName")}</label>
                                <input id="stream-provider-name" className="form-control" style={modalStyles.input} required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })}/>
                            </div>
                            <div className="form-group" style={{ marginBottom: 12 }}>
                                <label htmlFor="stream-provider-client-id" style={modalStyles.label}>{t("streamProvidersPage.oauthClientId")}</label>
                                <input id="stream-provider-client-id" className="form-control" style={modalStyles.input} required value={form.clientId} onChange={(event) => setForm({ ...form, clientId: event.target.value })}/>
                            </div>
                            <div className="form-group" style={{ marginBottom: 12 }}>
                                <label htmlFor="stream-provider-client-secret" style={modalStyles.label}>{t("streamProvidersPage.oauthClientSecret")}</label>
                                <input id="stream-provider-client-secret" className="form-control" style={modalStyles.input} type="password" required value={form.clientSecret} onChange={(event) => setForm({ ...form, clientSecret: event.target.value })}/>
                            </div>
                            <div className="form-group mb-0">
                                <label htmlFor="stream-provider-refresh-token" style={modalStyles.label}>{t("streamProvidersPage.oauthRefreshToken")}</label>
                                <input id="stream-provider-refresh-token" className="form-control" style={modalStyles.input} type="password" required value={form.refreshToken} onChange={(event) => setForm({ ...form, refreshToken: event.target.value })}/>
                            </div>
                            {editing && <div style={modalStyles.hint}>{t("streamProvidersPage.replaceCredentialsHint")}</div>}
                        </> : <div style={{ ...modalStyles.hint, marginTop: 0 }}>
                            {t("streamProvidersPage.angeonxProviderHint")}
                        </div>}
                        </div>
                    </div>
                    <div className="modal-footer" style={modalStyles.footer}>
                        <button type="button" className="btn" style={{ ...modalStyles.button, ...modalStyles.buttonGhost }} onClick={onClose} disabled={saving}>
                            <i className="fas fa-times"/>{t("streamProvidersPage.cancel")}
                        </button>
                        <button type="submit" className="btn" style={{ ...modalStyles.button, ...modalStyles.buttonPrimary }} disabled={saving}>
                            <i className={`fas ${saving ? "fa-spinner fa-spin" : "fa-check"}`}/>
                            {saving ? t("streamProvidersPage.saving") : (showCredentials ? t("streamProvidersPage.saveProvider") : t("streamProvidersPage.useAngeonxProvider"))}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </>;
}

export default function StreamProvidersPage() {
    const { t, language } = useTranslation();
    const { hasAnyOrgEnterprise } = useActiveProfileOrgProOrElite();
    const colors = useThemeColors();
    const { data, loading, refetch } = useQuery(PROVIDERS, {
        fetchPolicy: "network-only",
        skip: !hasAnyOrgEnterprise,
    });
    const [createProvider] = useMutation(CREATE_PROVIDER);
    const [updateProvider] = useMutation(UPDATE_PROVIDER);
    const [deleteProvider] = useMutation(DELETE_PROVIDER);
    const [testProvider] = useMutation(TEST_PROVIDER);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [page, setPage] = useState(1);
    const [testingProviderId, setTestingProviderId] = useState(null);
    const pageSize = 10;
    const providers = data?.youtubeProviders || [];
    const pages = Math.max(1, Math.ceil(providers.length / pageSize));
    const pageItems = providers.slice((page - 1) * pageSize, page * pageSize);
    const rowButtonStyle = {
        borderRadius: 12,
        padding: "8px 10px",
        fontWeight: 900,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        cursor: "pointer",
        whiteSpace: "nowrap",
    };

    const openCreate = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const openEdit = (provider) => {
        setEditing(provider);
        setModalOpen(true);
    };

    const saveProvider = async (form) => {
        try {
            const variables = {
                name: form.providerType === "angeonx" ? "AngeonX YouTube Provider" : form.name,
                provider: form.providerType,
                clientId: form.clientId,
                clientSecret: form.clientSecret,
                refreshToken: form.refreshToken,
                ...(form.id ? { id: Number(form.id) } : {}),
            };
            if (editing) await updateProvider({ variables });
            else await createProvider({ variables });
            await refetch();
            toast.success(t("streamProvidersPage.saved"));
            return true;
        } catch (error) {
            toast.error(error.message);
            return false;
        }
    };

    const removeProvider = async (provider) => {
        const providerName = provider.isAngeonxDefault ? t("streamProvidersPage.angeonxProvider") : provider.name;
        if (!window.confirm(t("streamProvidersPage.confirmDelete", { name: providerName }))) return;
        try {
            await deleteProvider({ variables: { id: Number(provider.id) } });
            await refetch();
            toast.success(t("streamProvidersPage.deleted"));
        } catch (error) {
            toast.error(error.message);
        }
    };

    const checkProvider = async (provider) => {
        setTestingProviderId(provider.id);
        try {
            const { data: result } = await testProvider({ variables: { id: Number(provider.id) } });
            const check = result?.testYoutubeProvider;
            await refetch();
            if (check?.success) toast.success(check.message || t("streamProvidersPage.checkAvailable"));
            else toast.error(check?.message || t("streamProvidersPage.checkFailed"));
        } catch (error) {
            toast.error(error.message);
        } finally {
            setTestingProviderId(null);
        }
    };

    const renderRow = (provider) => <tr key={provider.id}>
        <td>{provider.isAngeonxDefault ? t("streamProvidersPage.angeonxProvider") : provider.name}</td>
        <td>{provider.isAngeonxDefault ? t("streamProvidersPage.angeonxProvider") : t("streamProvidersPage.ownProvider")}</td>
        <td>{provider.isAngeonxDefault ? t("streamProvidersPage.platformAccount") : provider.streamingAccountId}</td>
        <td>
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: 999,
                padding: "6px 10px",
                fontSize: 12,
                fontWeight: 900,
                color: provider.availabilityStatus === "available" ? "#176b43" : provider.availabilityStatus === "unavailable" || provider.availabilityStatus === "check_failed" ? "#a52a2a" : "rgba(20,21,26,0.62)",
                background: provider.availabilityStatus === "available" ? "rgba(40,167,69,0.12)" : provider.availabilityStatus === "unavailable" || provider.availabilityStatus === "check_failed" ? "rgba(220,53,69,0.10)" : "rgba(0,0,0,0.06)",
                border: `1px solid ${provider.availabilityStatus === "available" ? "rgba(40,167,69,0.22)" : provider.availabilityStatus === "unavailable" || provider.availabilityStatus === "check_failed" ? "rgba(220,53,69,0.20)" : "rgba(0,0,0,0.08)"}`,
            }} title={provider.availabilityMessage || undefined}>
                {provider.availabilityStatus === "available" ? t("streamProvidersPage.available") : provider.availabilityStatus === "unavailable" ? t("streamProvidersPage.unavailable") : provider.availabilityStatus === "check_failed" ? t("streamProvidersPage.checkFailedStatus") : t("streamProvidersPage.notChecked")}
            </span>
            {provider.availabilityCheckedAt && <div style={{ marginTop: 5, fontSize: 11.5, color: "rgba(20,21,26,0.58)", fontWeight: 700 }}>
                {t("streamProvidersPage.checked", { date: new Date(provider.availabilityCheckedAt).toLocaleString(language) })}
            </div>}
        </td>
        <td className="text-right">
            <button type="button" style={{ ...rowButtonStyle, border: "1px solid rgba(0,0,0,0.10)", background: colors.primary, color: colors.textOnPrimary, opacity: testingProviderId !== null ? 0.65 : 1 }} disabled={testingProviderId !== null} onClick={() => checkProvider(provider)}>
                {testingProviderId === provider.id ? <><i className="fas fa-spinner fa-spin"/>{t("streamProvidersPage.checking")}</> : <><i className="fas fa-signal"/>{t("streamProvidersPage.checkLive")}</>}
            </button>
            {!provider.isAngeonxDefault && <>
                <button type="button" style={{ ...rowButtonStyle, marginLeft: 8, border: "1px solid rgba(0,0,0,0.12)", background: "#fff", color: "#14151a" }} onClick={() => openEdit(provider)}><i className="fas fa-pen"/>{t("common.edit")}</button>
                <button type="button" style={{ ...rowButtonStyle, marginLeft: 8, border: "1px solid rgba(220,53,69,0.22)", background: "rgba(220,53,69,0.06)", color: "#a52a2a" }} onClick={() => removeProvider(provider)}><i className="fas fa-trash"/>{t("common.delete")}</button>
            </>}
        </td>
    </tr>;

    return <div className="content-wrapper">
        <section className="content-header"><div className="container-fluid"><h1>{t("streamProvidersPage.title")}</h1></div></section>
        <section className="content"><div className="container-fluid">
            {!hasAnyOrgEnterprise ? <UpgradeNotice
                planLabel="ENTERPRISE"
                title={t("streamProvidersPage.title")}
                subtitle={t("streamProvidersPage.upgradeSubtitle")}
                ctaLabel={t("streamProvidersPage.viewPlans")}
                to="/subscription/plans"
            >
                {t("streamProvidersPage.upgradeMessage")} {" "}
                <UpgradeNoticeLink to="/subscription/plans">{t("streamProvidersPage.upgradeLink")}</UpgradeNoticeLink> {t("streamProvidersPage.upgradeSuffix")}
            </UpgradeNotice> : <>
                <div role="note" style={{ marginBottom: 16, padding: "12px 14px", borderRadius: 14, border: `1px solid ${alphaFromHex(colors.primary, 0.20)}`, background: alphaFromHex(colors.primary, 0.07), color: "rgba(20,21,26,0.78)", fontWeight: 700 }}>
                    {t("streamProvidersPage.quotaNotice")}
                </div>
                <PaginatedFlatTable
                title={t("streamProvidersPage.title")}
                headers={[t("streamProvidersPage.nameHeader"), t("streamProvidersPage.providerHeader"), t("streamProvidersPage.accountHeader"), t("streamProvidersPage.availabilityHeader"), t("streamProvidersPage.actionsHeader")]}
                items={pageItems}
                totalCount={providers.length}
                renderRow={renderRow}
                page={page}
                pages={pages}
                onPageChange={setPage}
                loading={loading}
                onAdd={openCreate}
                />
            </>}
        </div></section>
        <StreamProviderModal
            show={modalOpen && hasAnyOrgEnterprise}
            provider={editing}
            onClose={() => setModalOpen(false)}
            onSave={saveProvider}
        />
    </div>;
}
